import React from 'react'
import BenefitSection from './BenefitSection'
import ServiceCard from './ServiceCard'
import ScheduleDialog from './ScheduleDialog'
import { useBenefits } from '../BenefitsProvider'
import { useTranslation } from '../../../hooks/useTranslation'
import CoachServicesPage from './CoachServicesPage'
import SelectTabNav from './SelectTabNav'
import { SERVICE_REQUEST_ICONS } from '../ServiceRequestIcons'
import SelectUpcomingMeetings from './SelectUpcomingMeetings'
import ResumeTailoringDialog from './ResumeTailoringDialog'

const SelectCoachServicesPage = () => {
  const {
    current_user,
    coach,
    serviceRequestList,
    selectStrengthsAssessmentEnabled,
  } = useBenefits()
  const { t } = useTranslation()

  const { isLiaison, liaison_image_url } = coach

  const white = 'text-white hover:!underline hover:!text-white'

  const orientationCallRequest =
    serviceRequestList?.['ServiceRequest::OrientationCall']

  const orientationCallViewed = orientationCallRequest?.viewed === true

  const orientationCallHash = {
    requestType: 'ServiceRequest::OrientationCall',
    title: t('service_card.coach_introduction.title'),
    description: t('service_card.coach_introduction.description'),
    buttonText: t('service_card.coach_introduction.button_text'),
    iconType: SERVICE_REQUEST_ICONS['ServiceRequest::OrientationCall'],
    buttonUrl: '/coach_services/professional_services/orientation_call',
  }

  const generalCheckInHash = {
    requestType: 'ServiceRequest::GeneralCheckIn',
    title: t('service_card.general_check_in.title'),
    description: t('service_card.general_check_in.description'),
    buttonText: t('service_card.general_check_in.button_text'),
    iconType: SERVICE_REQUEST_ICONS['ServiceRequest::GeneralCheckIn'],
    buttonUrl: '/coach_services/professional_services/general_check_in',
  }

  const introCallServiceType = orientationCallViewed
    ? 'ServiceRequest::GeneralCheckIn'
    : 'ServiceRequest::OrientationCall'

  const resumeTailoringDialogShow =
    !serviceRequestList['ServiceRequest::ResumeReview']?.completed

  let services = {
    intro_call: {
      ...(orientationCallViewed ? generalCheckInHash : orientationCallHash),
      classes: 'bg-intoo-select',
      linkColor: white,
      photoUrl: isLiaison
        ? liaison_image_url
        : coach.transparent_photo || coach.photo,
      liaison: isLiaison,
      fullWidth: true,
      coachCard: true,
    },
    core: [
      orientationCallViewed ? orientationCallHash : generalCheckInHash,
      {
        requestType: 'ServiceRequest::ResumeWriting',
        title: t('service_card.resume_writing.title'),
        description: t('service_card.resume_writing.description'),
        buttonText: t('service_card.resume_writing.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::ResumeWriting'],
        buttonUrl: '/coach_services/professional_services/resume_writing',
      },
      {
        requestType: 'ServiceRequest::ResumeReview',
        title: t('service_card.resume_development.title'),
        description: t('service_card.resume_development.description'),
        buttonText: t('service_card.resume_development.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::ResumeReview'],
        buttonUrl: '/coach_services/resume_review',
      },
      {
        requestType: 'ServiceRequest::ResumeTailoring',
        title: t('service_card.resume_tailoring.title'),
        description: t('service_card.resume_tailoring.description'),
        buttonText: t('service_card.resume_tailoring.button_text'),
        actionRenderer: resumeTailoringDialogShow
          ? (buttonProps) => <ResumeTailoringDialog {...buttonProps} />
          : undefined,
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::ResumeTailoring'],
        buttonUrl: '/coach_services/professional_services/resume_tailoring',
      },
      {
        requestType: 'ServiceRequest::CoverLetterReview',
        title: t('service_card.cover_letter_review.title'),
        description: t('service_card.cover_letter_review.description'),
        buttonText: t('service_card.cover_letter_review.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::CoverLetterReview'],
        buttonUrl: '/coach_services/professional_services/cover_letter_review',
      },
      {
        requestType: 'ServiceRequest::LinkedInReview',
        title: t('service_card.linkedin_optimization.title'),
        description: t('service_card.linkedin_optimization.description'),
        buttonText: t('service_card.linkedin_optimization.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::LinkedInReview'],
        buttonUrl: '/coach_services/professional_services/linked_in_review',
      },
      {
        requestType: 'ServiceRequest::RecruiterGuidanceSupport',
        title: t('service_card.recruiter_guidance_support.title'),
        description: t('service_card.recruiter_guidance_support.description'),
        buttonText: t('service_card.recruiter_guidance_support.button_text'),
        iconType:
          SERVICE_REQUEST_ICONS['ServiceRequest::RecruiterGuidanceSupport'],
        buttonUrl:
          '/coach_services/professional_services/recruiter_guidance_support',
      },
      {
        requestType: 'ServiceRequest::MockInterview',
        title: t('service_card.mock_interview.title'),
        description: t('service_card.mock_interview.description'),
        buttonText: t('service_card.mock_interview.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::MockInterview'],
        buttonUrl: '/coach_services/professional_services/mock_interview',
      },
      {
        requestType: 'ServiceRequest::InterviewPrep',
        title: t('service_card.interview_prep.title'),
        description: t('service_card.interview_prep.description'),
        buttonText: t('service_card.interview_prep.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::InterviewPrep'],
        buttonUrl: '/coach_services/professional_services/interview_prep',
      },
      {
        requestType: 'ServiceRequest::OnboardingSupport',
        title: t('service_card.onboarding_support.title_select'),
        description: t('service_card.onboarding_support.description'),
        buttonText: t('service_card.onboarding_support.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::OnboardingSupport'],
        buttonUrl: '/coach_services/professional_services/onboarding_support',
      },
    ],
    strategic: [
      {
        requestType: 'ServiceRequest::JobSearchStrategy',
        title: t('service_card.job_search_strategy.title'),
        description: t('service_card.job_search_strategy.description'),
        buttonText: t('service_card.job_search_strategy.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::JobSearchStrategy'],
        buttonUrl: '/coach_services/professional_services/job_search_strategy',
      },
      {
        requestType: 'ServiceRequest::NetworkingStrategy',
        title: t('service_card.networking_strategy.title'),
        description: t('service_card.networking_strategy.description'),
        buttonText: t('service_card.networking_strategy.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::NetworkingStrategy'],
        buttonUrl: '/coach_services/professional_services/networking_strategy',
      },
      {
        requestType: 'ServiceRequest::CareerPivotPlanning',
        title: t('service_card.career_pivot_planning.title'),
        description: t('service_card.career_pivot_planning.description'),
        buttonText: t('service_card.career_pivot_planning.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::CareerPivotPlanning'],
        buttonUrl:
          '/coach_services/professional_services/career_pivot_planning',
      },
      {
        requestType: 'ServiceRequest::StrengthsAssessment',
        title: t('service_card.strengths_assessment.title'),
        description: t('service_card.strengths_assessment.description'),
        buttonText: t('service_card.strengths_assessment.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::StrengthsAssessment'],
        buttonUrl: '/coach_services/professional_services/strengths_assessment',
      },
      {
        requestType: 'ServiceRequest::JobSearchBurnout',
        title: t('service_card.job_search_burnout.title'),
        description: t('service_card.job_search_burnout.description'),
        buttonText: t('service_card.job_search_burnout.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::JobSearchBurnout'],
        buttonUrl: '/coach_services/professional_services/job_search_burnout',
      },
      {
        requestType: 'ServiceRequest::TargetedResearch',
        title: t('service_card.targeted_research.title'),
        description: t('service_card.targeted_research.description'),
        buttonText: t('service_card.targeted_research.button_text_select'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::TargetedResearch'],
        buttonUrl: '/coach_services/professional_services/targeted_research',
      },
      {
        requestType: 'ServiceRequest::SalaryResearch',
        title: t('service_card.salary_research.title'),
        description: t('service_card.salary_research.description'),
        buttonText: t('service_card.salary_research.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::SalaryResearch'],
        buttonUrl: '/coach_services/professional_services/salary_research',
      },
      {
        requestType: 'ServiceRequest::NegotiationStrategy',
        title: t('service_card.negotiation_strategy.title'),
        description: t('service_card.negotiation_strategy.description'),
        buttonText: t('service_card.negotiation_strategy.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::NegotiationStrategy'],
        buttonUrl: '/coach_services/professional_services/negotiation_strategy',
      },
      {
        requestType: 'ServiceRequest::ExecutiveBio',
        title: t('service_card.executive_bio.title'),
        description: t('service_card.executive_bio.description'),
        buttonText: t('service_card.executive_bio.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::ExecutiveBio'],
        buttonUrl: '/coach_services/professional_services/executive_bio',
      },
    ],
    specialized: [
      {
        requestType: 'ServiceRequest::BoardSearch',
        title: t('service_card.board_search.title'),
        description: t('service_card.board_search.description'),
        buttonText: t('service_card.board_search.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::BoardSearch'],
        buttonUrl: '/coach_services/professional_services/board_search',
      },
      {
        requestType: 'ServiceRequest::BusinessCardCreation',
        title: t('service_card.business_card_creation.title'),
        description: t('service_card.business_card_creation.description'),
        buttonText: t('service_card.business_card_creation.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::BusinessCardCreation'],
        buttonUrl:
          '/coach_services/professional_services/business_card_creation',
      },
      {
        requestType: 'ServiceRequest::RetirementSupport',
        title: t('service_card.retirement_support.title'),
        description: t('service_card.retirement_support.description'),
        buttonText: t('service_card.retirement_support.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::RetirementSupport'],
        buttonUrl: '/coach_services/professional_services/retirement_support',
      },
    ],
  }

  // Filter out StrengthsAssessment if not enabled in both allServices and the services object for SelectTabNav
  const filteredStrategic = selectStrengthsAssessmentEnabled
    ? services.strategic
    : services.strategic.filter(
        (s) => s.requestType !== 'ServiceRequest::StrengthsAssessment',
      )

  const filteredServices = {
    ...services,
    strategic: filteredStrategic,
  }

  const allServices = [
    services.intro_call,
    ...services.core,
    ...filteredStrategic,
    ...services.specialized,
  ]

  return (
    <CoachServicesPage>
      <BenefitSection className="md:mb-4">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <ServiceCard
            {...services.intro_call}
            introCallServiceType={introCallServiceType}
            isIntroCard={true}
            disableLeftAlignment={true}
          />
          <SelectUpcomingMeetings className="md:col-span-2" />
        </div>
      </BenefitSection>
      <SelectTabNav services={filteredServices}>
        <ScheduleDialog
          current_user={current_user}
          coach={coach}
          services={allServices}
        />
      </SelectTabNav>
    </CoachServicesPage>
  )
}
export default SelectCoachServicesPage
